// src/helpers/analytics.js
// Google Analytics 4 Helper - Siguiendo COPILOT.md

/**
 * Google Analytics 4 Helper
 * 
 * Configuración y tracking de eventos para terserah.id
 * 
 * Uso:
 * import { trackEvent, trackPageView } from './helpers/analytics';
 * 
 * trackEvent('view_item', { ad_id: '123', category: 'electronics' });
 */

// Inicializar GA4
export const initGA4 = (measurementId) => {
  if (typeof window === 'undefined' || !measurementId) return;

  // Cargar gtag.js
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  // Inicializar dataLayer
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false, // Lo haremos manualmente
    cookie_flags: 'SameSite=None;Secure'
  });

  // Exponer gtag globalmente
  window.gtag = gtag;

  console.log('✅ Google Analytics 4 initialized:', measurementId);
};

// Track Page View
export const trackPageView = (path, title) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href
  });
};

// Track Event Genérico
export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, {
    ...params,
    timestamp: new Date().toISOString()
  });
};

// Eventos específicos de Terserah

export const analytics = {
  // Anuncios
  viewAd: (adId, category, price) => {
    trackEvent('view_item', {
      item_id: adId,
      item_category: category,
      value: price,
      currency: 'IDR'
    });
  },

  createAd: (adId, category, price) => {
    trackEvent('create_ad', {
      item_id: adId,
      item_category: category,
      value: price,
      currency: 'IDR'
    });
  },

  editAd: (adId) => {
    trackEvent('edit_ad', {
      item_id: adId
    });
  },

  deleteAd: (adId) => {
    trackEvent('delete_ad', {
      item_id: adId
    });
  },

  markAsSold: (adId, finalPrice) => {
    trackEvent('mark_as_sold', {
      item_id: adId,
      value: finalPrice,
      currency: 'IDR'
    });
  },

  // Búsqueda
  search: (searchTerm, category, resultsCount) => {
    trackEvent('search', {
      search_term: searchTerm,
      item_category: category,
      results_count: resultsCount
    });
  },

  filterSearch: (filters) => {
    trackEvent('filter_search', {
      filters: JSON.stringify(filters)
    });
  },

  // Interacciones
  contactSeller: (adId, contactMethod) => {
    trackEvent('contact_seller', {
      item_id: adId,
      method: contactMethod // 'chat', 'whatsapp', 'phone'
    });
  },

  makeOffer: (adId, offerAmount, originalPrice) => {
    trackEvent('make_offer', {
      item_id: adId,
      offer_amount: offerAmount,
      original_price: originalPrice,
      discount_percentage: ((originalPrice - offerAmount) / originalPrice * 100).toFixed(2)
    });
  },

  acceptOffer: (adId, offerAmount) => {
    trackEvent('accept_offer', {
      item_id: adId,
      value: offerAmount,
      currency: 'IDR'
    });
  },

  rejectOffer: (adId, offerAmount) => {
    trackEvent('reject_offer', {
      item_id: adId,
      offer_amount: offerAmount
    });
  },

  // Favoritos
  addToFavorites: (adId, category) => {
    trackEvent('add_to_wishlist', {
      item_id: adId,
      item_category: category
    });
  },

  removeFromFavorites: (adId) => {
    trackEvent('remove_from_wishlist', {
      item_id: adId
    });
  },

  // Usuario
  signUp: (method) => {
    trackEvent('sign_up', {
      method: method // 'email', 'google', 'apple'
    });
  },

  login: (method) => {
    trackEvent('login', {
      method: method
    });
  },

  completeProfile: () => {
    trackEvent('complete_profile');
  },

  verifyPhone: () => {
    trackEvent('verify_phone');
  },

  verifyEmail: () => {
    trackEvent('verify_email');
  },

  // Reviews
  leaveReview: (userId, rating) => {
    trackEvent('leave_review', {
      user_id: userId,
      rating: rating
    });
  },

  // Social
  followUser: (userId) => {
    trackEvent('follow_user', {
      user_id: userId
    });
  },

  shareAd: (adId, method) => {
    trackEvent('share', {
      method: method, // 'facebook', 'twitter', 'whatsapp', 'copy'
      content_type: 'ad',
      item_id: adId
    });
  },

  // Errors
  reportError: (errorType, errorMessage) => {
    trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      error_type: errorType
    });
  },

  // Performance
  trackTiming: (category, variable, value) => {
    trackEvent('timing_complete', {
      name: variable,
      value: value,
      event_category: category
    });
  }
};

// Track de tiempo en página
export const trackTimeOnPage = () => {
  const startTime = Date.now();

  return () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000); // segundos
    trackEvent('time_on_page', {
      time_spent_seconds: timeSpent,
      page: window.location.pathname
    });
  };
};

// Enhanced eCommerce - Conversiones
export const trackConversion = (adId, price, category) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'purchase', {
    transaction_id: `txn_${adId}_${Date.now()}`,
    value: price,
    currency: 'IDR',
    items: [{
      item_id: adId,
      item_name: 'Ad Sold',
      item_category: category,
      price: price,
      quantity: 1
    }]
  });
};

const analyticsExports = {
  initGA4,
  trackPageView,
  trackEvent,
  analytics,
  trackTimeOnPage,
  trackConversion
};

export default analyticsExports;
